import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  injectAsync,
  input,
  OnDestroy,
  onIdle,
  viewChild,
} from '@angular/core';
import type * as LeafletTypes from 'leaflet';

export interface OsmPoi {
  id: string;
  name: string;
  category: 'shops' | 'education' | 'transport' | 'leisure' | 'services' | 'health';
  lat: number;
  lng: number;
  distanceMeters?: number;
  details?: string;
}

const POI_CATEGORY_METADATA: Record<string, { label: string; bg: string; icon: string; border: string }> = {
  shops: { label: 'Commerce', bg: '#0284c7', icon: '🛒', border: '#0369a1' },
  education: { label: 'Éducation', bg: '#d97706', icon: '🎓', border: '#b45309' },
  transport: { label: 'Transport', bg: '#16a34a', icon: '🚆', border: '#15803d' },
  leisure: { label: 'Loisir & Sport', bg: '#9333ea', icon: '⚽', border: '#7e22ce' },
  services: { label: 'Service public', bg: '#4f46e5', icon: '🏛️', border: '#4338ca' },
  health: { label: 'Santé', bg: '#e11d48', icon: '🏥', border: '#be123c' },
};

@Component({
  selector: 'aura-open-street-map',
  imports: [],
  templateUrl: './open-street-map.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block w-full h-full min-h-64',
  },
})
export class OpenStreetMapComponent implements OnDestroy, AfterViewInit {
  lat = input.required<number>();
  lng = input.required<number>();
  radius = input<number>(300);
  pois = input<OsmPoi[]>([]);
  activeCategories = input<string[]>([]);
  allowZoom = input<boolean>(true);
  scrollWheelZoom = input<boolean>(true);
  doubleClickZoom = input<boolean>(true);
  zoomControl = input<boolean>(true);
  interactive = input<boolean>(true);
  allowDrag = input<boolean>(true);
  showMarker = input<boolean>(true);

  mapContainer = viewChild<ElementRef<HTMLElement>>('mapContainer');

  // Prefetch Leaflet service in idle time to eliminate latency on first map render
  private leafletService = injectAsync(() => import('./leaflet.service').then((m) => m.LeafletService), {
    prefetch: onIdle,
  });

  private map: LeafletTypes.Map | undefined;
  private marker: LeafletTypes.Marker | undefined;
  private circle: LeafletTypes.Circle | undefined;
  private poiLayerGroup: LeafletTypes.LayerGroup | undefined;

  constructor() {
    effect(() => {
      // Trigger update when signals change
      this.lat();
      this.lng();
      this.radius();
      this.allowZoom();
      this.scrollWheelZoom();
      this.doubleClickZoom();
      this.zoomControl();
      this.interactive();
      this.allowDrag();
      this.showMarker();

      // Attempt update (will be skipped if view not ready)
      void this.updateMap();
    });

    effect(() => {
      // Update POI markers when pois or activeCategories change
      this.pois();
      this.activeCategories();
      void this.updatePoiMarkers();
    });
  }

  ngAfterViewInit(): void {
    // View is ready, update map
    void this.updateMap();
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  private async loadLeaflet(): Promise<typeof LeafletTypes> {
    const service = await this.leafletService();
    return service.getLeaflet();
  }

  private async updateMap() {
    const container = this.mapContainer()?.nativeElement;
    if (!container) return;

    const lat = this.lat();
    const lng = this.lng();
    const radius = this.radius();

    if (!lat || !lng) return;

    const leaflet = await this.loadLeaflet();

    const isInteractive = this.interactive();
    const canZoom = isInteractive && this.allowZoom();
    const canScrollZoom = canZoom && this.scrollWheelZoom();
    const canDoubleClickZoom = canZoom && this.doubleClickZoom();
    const canDrag = isInteractive && this.allowDrag();
    const showZoomControl = canZoom && this.zoomControl();

    if (!this.map) {
      this.map = leaflet
        .map(container, {
          zoomControl: showZoomControl,
          scrollWheelZoom: canScrollZoom,
          doubleClickZoom: canDoubleClickZoom,
          touchZoom: canZoom,
          boxZoom: canZoom,
          keyboard: canZoom,
          dragging: canDrag,
        })
        .setView([lat, lng], 15);

      leaflet
        .tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
        })
        .addTo(this.map);

      this.poiLayerGroup = leaflet.layerGroup().addTo(this.map);
    } else {
      this.map.setView([lat, lng], 15);
      if (!this.poiLayerGroup) {
        this.poiLayerGroup = leaflet.layerGroup().addTo(this.map);
      }

      // Update interaction handlers dynamically
      if (canDrag) {
        this.map.dragging.enable();
      } else {
        this.map.dragging.disable();
      }

      if (canScrollZoom) {
        this.map.scrollWheelZoom.enable();
      } else {
        this.map.scrollWheelZoom.disable();
      }

      if (canDoubleClickZoom) {
        this.map.doubleClickZoom.enable();
      } else {
        this.map.doubleClickZoom.disable();
      }

      if (canZoom) {
        this.map.touchZoom.enable();
        this.map.boxZoom.enable();
        this.map.keyboard.enable();
      } else {
        this.map.touchZoom.disable();
        this.map.boxZoom.disable();
        this.map.keyboard.disable();
      }
    }

    // Fix for map rendering issues when container size changes
    requestAnimationFrame(() => {
      this.map?.invalidateSize();
    });

    // Clear previous layers
    if (this.marker) this.marker.remove();
    if (this.circle) this.circle.remove();

    // Always show circle around property
    this.circle = leaflet
      .circle([lat, lng], {
        color: '#e04f16',
        fillColor: '#e04f16',
        fillOpacity: 0.15,
        radius: radius,
      })
      .addTo(this.map);

    if (this.showMarker()) {
      this.marker = leaflet.marker([lat, lng]).addTo(this.map);
    }

    // Fit map to circle bounds with some padding
    const bounds = this.circle.getBounds();
    this.map.fitBounds(bounds, { padding: [40, 40] });

    void this.updatePoiMarkers();
  }

  private async updatePoiMarkers() {
    if (!this.map || !this.poiLayerGroup) return;

    const leaflet = await this.loadLeaflet();
    this.poiLayerGroup.clearLayers();

    const pois = this.pois();
    const activeCats = this.activeCategories();

    if (!pois.length || !activeCats.length) return;

    const filtered = pois.filter((p) => activeCats.includes(p.category));

    filtered.forEach((poi) => {
      const meta = POI_CATEGORY_METADATA[poi.category] || {
        label: 'Point d’intérêt',
        bg: '#64748b',
        icon: '📍',
        border: '#475569',
      };

      const customIcon = leaflet.divIcon({
        className: 'aura-poi-marker',
        html: `<div style="
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          background-color: ${meta.bg};
          color: white;
          border: 2px solid white;
          border-radius: 9999px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -2px rgba(0,0,0,0.2);
          font-size: 14px;
          line-height: 1;
          cursor: pointer;
        ">${meta.icon}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        popupAnchor: [0, -16],
      });

      const m = leaflet.marker([poi.lat, poi.lng], { icon: customIcon });
      m.bindPopup(`
        <div style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-size: 12px; line-height: 1.4; padding: 2px 4px; min-width: 150px;">
          <div style="font-weight: 700; color: #111827; margin-bottom: 2px;">${poi.name}</div>
          <div style="display: flex; align-items: center; gap: 6px; font-size: 11px; color: #4b5563;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 9999px; background-color: ${meta.bg};"></span>
            <span>${meta.label}</span>
            ${poi.distanceMeters != null ? `<span style="font-weight: 600; color: #111827;">• ~${Math.round(poi.distanceMeters)}m</span>` : ''}
          </div>
          ${poi.details ? `<div style="font-size: 10px; color: #6b7280; margin-top: 4px; border-top: 1px solid #f3f4f6; padding-top: 2px;">${poi.details}</div>` : ''}
        </div>
      `);

      this.poiLayerGroup?.addLayer(m);
    });
  }
}
